import type React from 'react';
import { useCallback } from 'react';
import { Flex, font } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES, Button } from '@lemonade-hq/nuis';
import styled from 'styled-components';
import parse from 'html-react-parser';
import { Section, MainHeader, SectionParagraph } from '@/components/shared/Content';
import HeroImage from '@/static/assets/dating/hero.svg';
import type { HeroProps } from '../renters/Hero';

const TopSectionContainer = styled(Section)`
  position: relative;
  background-color: transparent;
  z-index: 1;
`;

const StyledList = styled.ul`
  list-style: none;
  padding: 0;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: 5px 20px;
  flex-wrap: wrap;

  li {
    display: flex;
    align-items: center;
    gap: 10px;
  }
`;

const ListItemContent = styled.span`
  ${font('main', { fontSize: '1.6rem' })};
  display: flex;
  align-items: center;
  white-space: nowrap;
`;

const StyledImage = styled.img`
  pointer-events: none;
  object-fit: cover;
  margin-bottom: -100px;
  margin-top: -15px;
  z-index: 9;
  max-width: 550px;
  width: 100%;
  min-height: 242px;

  ${MEDIA_QUERIES.tabletAndDesktop`
        margin-top: -40px;
        min-height: 403px
    `}
`;

const CHECKS = ['Get peace of mind ', 'Super-fast & simple', 'Don’t stress about date costs', 'Save on self-care '];

const CheckMark: React.FC = () => {
  return (
    <svg width={11} height={10} fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M1 4.48 4.283 8.5 10 1.5"
        stroke="#FF0083"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const TopFoldTitle = styled(MainHeader)`
  text-transform: capitalize;
  position: relative;
  z-index: 9;

  ${MEDIA_QUERIES.tablet`
        white-space: initial;
    `}

  ${MEDIA_QUERIES.desktop`
        white-space: pre;
    `}
`;
const StyledButton = styled(Button)`
  min-width: 233px;
`;

const List: React.FC = () => {
  return (
    <StyledList>
      {CHECKS.map(check => (
        <li key={check}>
          <CheckMark />
          <ListItemContent>{check}</ListItemContent>
        </li>
      ))}
    </StyledList>
  );
};

export const Hero = ({ title, subtitle, ctaLabel }: HeroProps) => {
  const handleClick = useCallback(() => {
    const bundlesElement = document.querySelector('#video');

    if (!bundlesElement) return;

    const headerOffset = 70;
    const top = bundlesElement.getBoundingClientRect().top + window.pageYOffset - headerOffset;

    window.scrollTo({
      top,
      behavior: 'smooth',
    });
  }, []);

  return (
    <TopSectionContainer justifyContent="center" mt={[0, 80]}>
      <Flex gap="20px" flexDirection="column" justifyContent="center">
        <TopFoldTitle>{parse(title)}</TopFoldTitle>
        <SectionParagraph>{parse(subtitle)}</SectionParagraph>
        <List />
      </Flex>
      <StyledButton size="big" flavor="primary" glowing onClick={handleClick}>
        {ctaLabel}
      </StyledButton>
      <StyledImage src={HeroImage.src} alt="" />
    </TopSectionContainer>
  );
};

export default Hero;
